import { type LensCardState, type LensTokens, lensTokens } from '~/configs/unifiedAnalyticsTheme';

import type { Theme } from '@mui/material/styles';

import type { LensAggregate, LensCard, LensCardData, LensContract, LensDataset } from '~/types/leLens';

import { dayLabel, spanLabel } from '../lensCalendar';
import { formatLensDayLabel } from '../lensChart';

/**
 * Assumed backend query cap for a card's row set. Above this a result is not
 * a complete answer, so any total drawn from it must say so. Inferred from
 * the audit's "up to ten thousand" wording, not read from a backend constant:
 * `data.truncated` is the authoritative signal when the backend sends it, and
 * this only stands in when it does not.
 */
export const ASSUMED_TABLE_ROW_LIMIT = 10_000;

/** Suisse Works, for a metric card's hero number and the page title only,
 *  per the typography spec. Georgia remains the fallback when local font
 *  loading is unavailable. */
export const LENS_HERO_FONT_FAMILY = '"Lens Suisse Works", Georgia, serif';

/** How many table rows this card renders into the DOM before relying on the
 *  footer to say there is more. Well above ten, well short of the row cap, so
 *  scrolling a card never means rendering ten thousand table cells. */
export const TABLE_VISIBLE_ROW_CAP = 500;

export type LensCardStateInput = {
  /** This card's slice of the shared fetch response, once it has arrived. */
  data: LensCardData | undefined;
  /** Whether the dashboard-wide data query is still in flight. */
  isFetching: boolean;
  /** Row count after cross-filter selections, ranges and the card's view have
   *  been applied. A 'ready' status can still carry zero rows post-filter. */
  shapedRowCount: number;
  /** True when the dashboard-wide fetch failed. Without it a card that never
   *  received a result is indistinguishable from one that ran and found
   *  nothing, so a total outage rendered as a legitimate empty board. */
  hasLoadError?: boolean;
};

/**
 * The one place a card's data is turned into a visual state. Every branch
 * here corresponds to a state the shell renders distinctly; nothing falls
 * through to a default, which is what let a zero-row chart print a developer
 * diagnostic string before.
 */
export function resolveCardState({
  data,
  isFetching,
  shapedRowCount,
  hasLoadError,
}: LensCardStateInput): LensCardState {
  if (!data) {
    // No entry for this card yet. While the shared fetch is in flight that is
    // ordinary loading; once it has settled without one, the card has nothing
    // to show and is treated as empty rather than loading forever. A fetch
    // that failed is neither: an empty state there claims the query ran and
    // returned no rows, which is a number the reader would act on.
    if (isFetching) {
      return 'loading';
    }
    return hasLoadError ? 'error' : 'empty';
  }
  if (data.status === 'restricted') {
    return 'restricted';
  }
  if (data.status === 'error') {
    return 'error';
  }
  // Truncation over-rides "ready": a capped result is never a finished
  // answer, whatever its status otherwise says.
  if (data.truncated) {
    return 'truncated';
  }
  if (data.status === 'empty' || data.status === 'unconfigured') {
    return 'empty';
  }
  if (shapedRowCount === 0) {
    return 'empty';
  }
  return 'ready';
}

/**
 * Whether a string is fit to put in front of a business reader.
 *
 * The dashboard's own error handler answers a failed request with the thrown
 * error's message and its stack (svc-reporting src/lib/errorHandler.js), and
 * the request layer prefixes what it gets with "Network error received (status
 * code N)". Neither is readable, and a stack tells a reader nothing they can
 * act on. So a detail line is shown only when it looks like a sentence
 * somebody wrote on purpose.
 */
function isReadableDetail(text: string): boolean {
  if (text.length > 400) {
    return false;
  }
  // A stack frame, a file path, a bare exception class or an HTTP phrase.
  return !/(\n\s+at\s)|(\bat\s+\w+[.(].*:\d+:\d+)|(\/[\w./-]+\.(?:ts|js|tsx|jsx):\d+)|(status code\s*\d{3})|(\b(?:Error|Exception|Traceback)\b\s*:\s*\w+.*\n)/i.test(
    text,
  );
}

/**
 * What a failed card says, and what it is willing to show behind "Show
 * details". Nothing raw off the wire reaches the card face: an unreadable
 * message is dropped rather than printed, because a reader who cannot act on
 * a string is better served by no string.
 */
export function describeCardFailure(rawMessage: string | undefined): { message: string; detail?: string } {
  const message = 'This card could not load.';
  const text = rawMessage?.trim();
  if (!text || !isReadableDetail(text)) {
    return { message };
  }
  return { message, detail: text };
}

/**
 * What a card shows when the dashboard-wide fetch failed and no result for it
 * ever arrived. Carried on the card's own data shape so the board keeps its
 * cards and its layout: an errored fetch used to leave every card with no
 * entry at all, which resolveCardState read as "ran and found nothing" and
 * printed as "No rows for this period". A reader had no way to tell a real
 * zero from a Lens that never ran.
 */
export function cardDataForBoardFailure(cardId: string): LensCardData {
  return {
    cardId,
    status: 'error',
    message: 'The Lens could not reach its data. Refresh this card, or reload the Lens.',
  };
}

/** Whether this card's own status explains its emptiness as "not configured"
 *  rather than "ran and found nothing". Kept separate from resolveCardState
 *  because both cases render the same shell state; only the copy differs. */
export function isUnconfiguredCard(data: LensCardData | undefined): boolean {
  return data?.status === 'unconfigured';
}

/** The window a card's rows were resolved against, for the empty state's
 *  secondary line. Never invents a period: an unbounded card says so. */
export function formatResolvedWindow(range: { from?: string; to?: string } | undefined): string {
  if (!range?.from && !range?.to) {
    return 'No period filter applied';
  }
  if (range.from && range.to) {
    return spanLabel(range.from, range.to);
  }
  return range.from ? `From ${dayLabel(range.from)}` : `Through ${dayLabel(range.to)}`;
}

/** Plain row count, for a card that returned everything it has. */
export function formatRowCount(count: number): string {
  return `${count.toLocaleString()} ${count === 1 ? 'row' : 'rows'}`;
}

/** A card showing fewer rows than it has, but for legibility rather than a
 *  backend cap: the full set is already in memory and any total over it is
 *  still honest, so this is a plain notice, not the truncated banner. */
export function formatPartialViewNotice(shown: number, total: number): string {
  return `Showing ${shown.toLocaleString()} of ${total.toLocaleString()} rows`;
}

/**
 * The truncated-state banner. Copy matches the spec exactly when the true
 * total is known; when it is not, "of N rows" is dropped rather than printed
 * with a placeholder, since a literal unknown number is worse than none.
 */
export function formatTruncatedBanner(shown: number, total?: number): string {
  const shownText = shown.toLocaleString();
  const prefix =
    total !== undefined && total > shown
      ? `Showing ${shownText} of ${total.toLocaleString()} rows.`
      : `Showing ${shownText} rows.`;
  return `${prefix} Ordering is not guaranteed, so totals may be understated.`;
}

/** A compact, human-readable freshness receipt. Raw ISO timestamps waste
 * card space and read as implementation detail rather than provenance. */
export function formatCardFreshness(value: string | undefined, now: Date = new Date()): string | undefined {
  if (!value) {
    return undefined;
  }
  const refreshedAt = new Date(value);
  if (Number.isNaN(refreshedAt.getTime())) {
    return undefined;
  }
  const elapsedMinutes = Math.max(0, Math.floor((now.getTime() - refreshedAt.getTime()) / 60_000));
  if (elapsedMinutes < 1) {
    return 'Updated just now';
  }
  if (elapsedMinutes < 60) {
    return `Updated ${elapsedMinutes} min ago`;
  }
  const elapsedHours = Math.floor(elapsedMinutes / 60);
  if (elapsedHours < 24) {
    return `Updated ${elapsedHours}h ago`;
  }
  return `Updated ${formatLensDayLabel(refreshedAt)}`;
}

export type ContractChipInfo = {
  label: string;
  variant: 'filled' | 'outlined';
  bg?: string;
  color?: string;
  borderColor?: string;
  /** What the label rests on, so a reader can judge it rather than take it. */
  basis: string;
  /** 'warning' when the state is a reason not to use the number. */
  emphasis: 'normal' | 'warning';
  /** True only for the unmarked default. The shell renders this as plain,
   *  low-emphasis footer text (no border, no chip weight); a marked state is
   *  never quiet, whatever its emphasis. */
  quiet: boolean;
};

/**
 * Every card shows a governance signal, contracted or not: a card with no chip reads as certified
 * by omission, which is the exact silence the spec forbids. The unmarked default is deliberately
 * quiet (plain footer text, no border) rather than a chip with warning weight, since it states a
 * fact ("nothing is claimed here") rather than flagging an exception: most cards are unmarked
 * today, and that must not read as most cards having a problem.
 *
 * The marked labels say "marked" on purpose. `card.contract` is a value an author sets in the
 * dashboard's own definition, and nothing verifies it: the API validates it against a static enum
 * (le-lens/schema.ts) and never compares it to le-data's metric registry, and the query path never
 * reads it before executing. A bare "Certified" reads as a statement by the system about the
 * number, which is the one claim this field cannot support. Saying who marked it keeps the signal
 * useful without inventing an assurance. When a registry-backed status exists, that is a different
 * state and deserves a different word.
 */
export function contractChipInfo(tokens: LensTokens, contract: LensContract | undefined): ContractChipInfo {
  const unverified = 'Set in this Lens’s definition. Not checked against the metric registry.';
  switch (contract) {
    case 'provisional':
      return {
        label: 'Marked provisional',
        variant: 'filled',
        bg: tokens.provisionalBg,
        color: tokens.provisionalText,
        basis: unverified,
        emphasis: 'normal',
        quiet: false,
      };
    case 'blocked':
      return {
        label: 'Marked blocked',
        variant: 'filled',
        bg: tokens.blockedBg,
        color: tokens.blockedText,
        basis: `${unverified} This number is marked as not fit for use.`,
        emphasis: 'warning',
        quiet: false,
      };
    default:
      return {
        label: 'Uncertified',
        variant: 'outlined',
        basis: 'No contract state is set on this card, so nothing is claimed about this number.',
        emphasis: 'normal',
        quiet: true,
      };
  }
}

export function contractChipInfoForTheme(theme: Theme, contract: LensContract | undefined): ContractChipInfo {
  return contractChipInfo(lensTokens(theme), contract);
}

/** Where a card's rows came from, read off the spec rather than the fetch: the
 *  dataset it names is known the moment the card is defined, so this never has
 *  to wait on data to say something. */
export function resolveCardSource(card: LensCard, datasets: Array<LensDataset>): string {
  if (!card.datasetId) {
    return 'No source configured';
  }
  const dataset = datasets.find((entry) => entry.id === card.datasetId);
  return dataset ? dataset.name : 'Unknown shared dataset';
}

export type SampledRows<T> = { rows: Array<T>; sampled: boolean };

/**
 * Evenly downsamples a row set above a ceiling rather than drawing every
 * mark. Order is preserved (a time series stays a time series) and the final
 * row is always kept so a trend never appears to stop short of where the
 * data actually ends.
 */
export function sampleRows<T>(rows: Array<T>, ceiling: number): SampledRows<T> {
  if (ceiling <= 0 || rows.length <= ceiling) {
    return { rows, sampled: false };
  }
  const step = rows.length / ceiling;
  const picked: Array<T> = [];
  for (let index = 0; index < ceiling; index += 1) {
    picked.push(rows[Math.floor(index * step)]);
  }
  picked[picked.length - 1] = rows[rows.length - 1];
  return { rows: picked, sampled: true };
}

/** A short label for how many rows fed a metric's headline, so the footer can
 *  say "summed from 42 rows" instead of leaving the reduction invisible. */
export function formatMetricProvenance(rowCount: number, aggregate: LensAggregate): string {
  if (rowCount <= 1) {
    return '1 row';
  }
  const verbs: Record<LensAggregate, string> = {
    sum: 'Summed',
    avg: 'Averaged',
    count: 'Counted',
    countDistinct: 'Counted distinct',
    min: 'Minimum of',
    max: 'Maximum of',
    first: 'First of',
  };
  return `${verbs[aggregate]} from ${rowCount.toLocaleString()} rows`;
}
